"use client";

import type { BrowserState } from "@/lib/search/url-state";
import type { FilterSelection } from "@/lib/search/parser";
import { Chip } from "@/components/ui/chip";
import { Segmented } from "@/components/ui/segmented";
import { cn } from "@/lib/utils";

interface ActiveFiltersProps {
  state: BrowserState;
  onChange: (next: Partial<BrowserState>) => void;
  className?: string;
}

const FIELD_LABELS: Record<FilterSelection["field"], string> = {
  author: "Author",
  categories: "Category",
  traits: "Trait",
  keywords: "Keyword",
};

const SEARCH_FIELD_LABELS: Record<string, string> = {
  title: "Title",
  author: "Author",
  ai_summary: "Summary",
  description: "Description",
  effects_text: "Effects",
  sections_text: "Sections",
};

/**
 * Compact, removable chips for every active filter, grouped by field. A
 * group with two or more values exposes its any/all matching inline.
 */
export function ActiveFilters({ state, onChange, className }: ActiveFiltersProps) {
  const hasFilters = state.filters.some((f) => f.values.length > 0);
  const hasFields = state.fields.length > 0;
  if (!hasFilters && !hasFields) return null;

  const removeValue = (field: FilterSelection["field"], value: string) => {
    const next = state.filters
      .map((f) => (f.field === field ? { ...f, values: f.values.filter((v) => v !== value) } : f))
      .filter((f) => f.values.length > 0);
    onChange({ filters: next });
  };

  const setOperator = (field: FilterSelection["field"], operator: "and" | "or") => {
    onChange({
      filters: state.filters.map((f) => (f.field === field ? { ...f, operator } : f)),
    });
  };

  return (
    <div
      className={cn("flex flex-wrap items-center gap-x-2 gap-y-1.5", className)}
      aria-label="Active filters"
    >
      {hasFields && (
        <Chip
          tone="neutral"
          prefix="Searching"
          onRemove={() => onChange({ fields: [] })}
          removeLabel="Search all fields"
        >
          {state.fields.map((f) => SEARCH_FIELD_LABELS[f] ?? f).join(", ")}
        </Chip>
      )}

      {state.filters.map((filter) => {
        if (filter.values.length === 0) return null;
        const multi = filter.values.length > 1;
        return (
          <div
            key={filter.field}
            className={cn(
              "flex flex-wrap items-center gap-1.5",
              multi && "rounded-full border border-dashed border-line py-0.5 pr-0.5 pl-1.5"
            )}
          >
            {multi && (
              <Segmented
                size="xs"
                aria-label={`${FIELD_LABELS[filter.field]} matching`}
                value={filter.operator}
                onChange={(op) => setOperator(filter.field, op)}
                options={[
                  {
                    value: "or",
                    label: "any",
                    title: "Match any selected value",
                  },
                  {
                    value: "and",
                    label: "all",
                    title: "Match every selected value",
                  },
                ]}
              />
            )}
            {filter.values.map((value) => (
              <Chip
                key={`${filter.field}:${value}`}
                prefix={FIELD_LABELS[filter.field]}
                onRemove={() => removeValue(filter.field, value)}
                removeLabel={`Remove ${FIELD_LABELS[filter.field].toLowerCase()} ${value}`}
              >
                {value}
              </Chip>
            ))}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => onChange({ filters: [], fields: [] })}
        className="h-7 rounded-full px-2 text-[12.5px] font-medium text-ink-3 transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
      >
        Clear all
      </button>
    </div>
  );
}
